import { eulerPartnerPoints } from '@/config/chain/points'
import type { IsolatedPartnerPointsPerPair } from '@/config/chain/types'
import { VAULT_LENS_ADDRESS, vaultLensAbi } from '@/config/euler-contracts'
import { type NormalizedTokenInfo, getTokenFromMap, useEulerIndexerELends, useTokensInfo } from '@/domain/euler'
import { WHYPE_ADDRESS_HYPEREVM } from '@/domain/euler/nativeHype'
import { extractVaultLensPrice } from '@/domain/euler/shared/onchainHelpers'
import { FALLBACK_SUBACCOUNT_RANGE, getSubAccountAddress } from '@/domain/euler/shared/subAccounts'
import { useSubaccountStore } from '@/domain/euler/subaccounts'
import { getEntities } from '@/features/evault-details/data/entities'
import { type VaultMarket, getVaultMarket } from '@/features/evault-details/data/products'
import { selectGovernorsFromAddresses } from '@/features/evault-details/logic/selectGovernors'
import { keepPreviousData } from '@tanstack/react-query'
import { calculateMaxMultiplier, calculateMaxRoe } from './leverageCalculations'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { eulerVaultConnectorAbi } from 'abis/evm/eulerVaultConnector'
import { useMemo } from 'react'
import { type Address, formatUnits, zeroAddress } from 'viem'
import { erc20Abi } from 'viem'
import { useBalance, useReadContract, useReadContracts } from 'wagmi'
import { BORROW_VAULT_CHAIN_ID, useVaultList } from './useVaultList'
import { useVaults } from './useVaults'

export interface BorrowPair {
  collateral: Address
  liability: Address
  collateralUnderlying?: Address
  liabilityUnderlying?: Address
  collateralSymbol?: string
  collateralDecimals?: number
  liabilitySymbol?: string
  liabilityDecimals?: number
  collateralTokenInfo?: NormalizedTokenInfo
  liabilityTokenInfo?: NormalizedTokenInfo
  collateralBalance?: bigint
  maxLTV?: bigint
  liquidationLTV?: bigint
  borrowAPY?: number
  supplyAPY?: number
  collateralSupplyAPY?: number
  liabilitySupplyAPY?: number
  /** Precomputed max ROE (Return on Equity) for leverage mode sorting/display */
  maxRoe?: number
  utilization?: number
  liquidity?: bigint
  liquidityUSD?: number
  hasPosition?: boolean
  userCollateralShares?: bigint
  userDebtOwed?: bigint
  userBorrowAmount?: number
  userBorrowAmountUsd?: number
  userCollateralDeposit?: number
  userCollateralDepositUsd?: number
  collateralCuratorName?: string
  collateralMarket?: VaultMarket
  liabilityCuratorName?: string
  liabilityMarket?: VaultMarket
  firstSubAccountWithPosition?: number
  partnerPoints?: IsolatedPartnerPointsPerPair[]
  healthFactor?: number
}

export function useBorrowPairs(account?: Address) {
  const { data: vaultList = [], isLoading: isVaultListLoading } = useVaultList()

  // Get active subaccount IDs from the global discovery cache
  const { activeSubaccountIds } = useSubaccountStore()

  const vaults = useVaults({
    queries: vaultList.map((vault) => ({
      address: vault,
      refetchOnMount: false,
    })),
  })

  // Create a set of verified vault addresses for quick lookup
  const verifiedVaultSet = useMemo(() => {
    return new Set(vaultList.map((addr) => addr.toLowerCase()))
  }, [vaultList])

  // Get all unique collateral-liability pairs with their vault data
  // Only include pairs where BOTH collateral and liability are verified vaults
  const pairs = useMemo(() => {
    const pairMap = new Map<
      string,
      BorrowPair & {
        _cash?: bigint
        _borrows?: bigint
        _borrowAPY?: bigint
        _supplyAPY?: bigint
      }
    >()

    for (const { data: vaultData } of vaults) {
      if (!vaultData) continue

      const liability = vaultData.vault
      const collateralLTVInfo = vaultData.collateralLTVInfo || []
      const irmInfo = vaultData.irmInfo?.interestRateInfo?.[0]

      for (const { collateral, borrowLtvRaw, liquidationLtvRaw } of collateralLTVInfo) {
        if (!verifiedVaultSet.has(collateral.toLowerCase())) {
          continue
        }

        const pairKey = `${collateral}-${liability}`

        if (!pairMap.has(pairKey)) {
          pairMap.set(pairKey, {
            collateral,
            liability,
            maxLTV: borrowLtvRaw,
            liquidationLTV: liquidationLtvRaw,
            // Store IRM data directly from the vault
            _cash: irmInfo?.cash,
            _borrows: irmInfo?.borrows,
            _borrowAPY: irmInfo?.borrowAPY,
            _supplyAPY: irmInfo?.supplyAPY,
          })
        } else {
          const existing = pairMap.get(pairKey)!
          // Use the higher LTV if multiple exist
          if (borrowLtvRaw > (existing.maxLTV || 0n)) {
            existing.maxLTV = borrowLtvRaw
            existing.liquidationLTV = liquidationLtvRaw
          }
        }
      }
    }

    return Array.from(pairMap.values())
  }, [vaults, verifiedVaultSet])

  // Get unique vault addresses to fetch their underlying assets
  const vaultAddresses = useMemo(() => {
    const addresses = new Set<Address>()
    for (const pair of pairs) {
      addresses.add(pair.collateral)
      addresses.add(pair.liability)
    }
    return Array.from(addresses)
  }, [pairs])

  // Fetch underlying asset addresses and governor addresses for each vault
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const vaultDataContracts: any[] = useMemo(() => {
    return vaultAddresses.flatMap((address) => [
      {
        address,
        abi: eVaultImplementationAbi,
        functionName: 'asset',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
      {
        address,
        abi: eVaultImplementationAbi,
        functionName: 'governorAdmin',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
    ])
  }, [vaultAddresses])

  const { data: vaultDataResults, isLoading: isUnderlyingLoading } = useReadContracts({
    contracts: vaultDataContracts,
    query: {
      enabled: vaultAddresses.length > 0,
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: Number.POSITIVE_INFINITY,
      placeholderData: keepPreviousData,
    },
  })

  // Create maps of vault address -> underlying asset and vault address -> governor
  const { vaultToUnderlying, vaultToGovernor } = useMemo(() => {
    const underlyingMap = new Map<Address, Address>()
    const governorMap = new Map<Address, Address>()

    vaultAddresses.forEach((vault, index) => {
      // Each vault has 2 contract calls: asset (index*2) and governorAdmin (index*2 + 1)
      const underlyingIndex = index * 2
      const governorIndex = index * 2 + 1

      const underlying = vaultDataResults?.[underlyingIndex]?.result as Address | undefined
      const governor = vaultDataResults?.[governorIndex]?.result as Address | undefined

      if (underlying) {
        underlyingMap.set(vault, underlying)
      }
      if (governor) {
        governorMap.set(vault, governor)
      }
    })

    return { vaultToUnderlying: underlyingMap, vaultToGovernor: governorMap }
  }, [vaultAddresses, vaultDataResults])

  // Get unique underlying asset addresses
  const underlyingAddresses = useMemo(() => {
    return Array.from(new Set(Array.from(vaultToUnderlying.values())))
  }, [vaultToUnderlying])

  // Fetch token info from Euler indexer (with onchain fallback)
  const { tokens: indexerTokens, isPending: isTokensLoading } = useTokensInfo(
    BORROW_VAULT_CHAIN_ID,
    underlyingAddresses,
  )

  // Fetch vault data from Euler indexer (provides totalApy including intrinsic APY)
  const { data: indexerVaults } = useEulerIndexerELends(BORROW_VAULT_CHAIN_ID)

  // Fetch native HYPE balance for WHYPE assets
  const hasWhypeAsset = underlyingAddresses.some((addr) => addr?.toLowerCase() === WHYPE_ADDRESS_HYPEREVM.toLowerCase())
  const { data: nativeBalance } = useBalance({
    address: account,
    chainId: BORROW_VAULT_CHAIN_ID,
    query: {
      enabled: !!account && account !== zeroAddress && hasWhypeAsset,
      refetchInterval: 60_000,
    },
  })

  // Fetch wallet balances for underlying assets (still needed onchain)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const balanceContracts: any[] = useMemo(() => {
    if (!account || account === zeroAddress) return []
    return underlyingAddresses.map((address) => ({
      address,
      abi: erc20Abi,
      functionName: 'balanceOf',
      args: [account],
      chainId: BORROW_VAULT_CHAIN_ID,
    }))
  }, [underlyingAddresses, account])

  const { data: balanceData, isLoading: isBalanceLoading } = useReadContracts({
    contracts: balanceContracts,
    query: {
      enabled: balanceContracts.length > 0,
      staleTime: 30_000,
      gcTime: 60_000,
      placeholderData: keepPreviousData,
    },
  })

  // Build map of underlying address -> balance (with WHYPE -> native HYPE swap)
  const addressToBalance = useMemo(() => {
    const map = new Map<Address, bigint>()
    if (balanceData && account && account !== zeroAddress) {
      for (let i = 0; i < underlyingAddresses.length; i++) {
        const address = underlyingAddresses[i]
        const result = balanceData[i]
        if (address && result?.status === 'success' && result.result) {
          let balance = result.result as bigint

          // Special handling for WHYPE: use native HYPE balance if it's higher
          if (
            address.toLowerCase() === WHYPE_ADDRESS_HYPEREVM.toLowerCase() &&
            nativeBalance &&
            nativeBalance.value > balance
          ) {
            balance = nativeBalance.value
          }

          map.set(address, balance)
        }
      }
    }
    return map
  }, [underlyingAddresses, balanceData, account, nativeBalance])

  // Fetch user position data (collateral shares + debt owed) for each pair
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const positionContracts: any[] = useMemo(() => {
    if (!account || account === zeroAddress) return []
    const contracts: any[] = []
    for (const pair of pairs) {
      contracts.push({
        address: pair.collateral,
        abi: eVaultImplementationAbi,
        functionName: 'balanceOf',
        args: [account],
        chainId: BORROW_VAULT_CHAIN_ID,
      })
      contracts.push({
        address: pair.liability,
        abi: eVaultImplementationAbi,
        functionName: 'debtOf',
        args: [account],
        chainId: BORROW_VAULT_CHAIN_ID,
      })
    }
    return contracts
  }, [pairs, account])

  const { data: positionData, isLoading: _isPositionLoading } = useReadContracts({
    contracts: positionContracts,
    query: {
      enabled: positionContracts.length > 0,
      staleTime: 30_000,
      gcTime: 60_000,
      placeholderData: keepPreviousData,
    },
  })

  // Build map of pair key -> position data
  const pairToPosition = useMemo(() => {
    const map = new Map<string, { collateralShares: bigint; debtOwed: bigint }>()
    if (positionData && account && account !== zeroAddress) {
      for (let i = 0; i < pairs.length; i++) {
        const pair = pairs[i]
        if (!pair) continue
        const collateralResult = positionData[i * 2]
        const debtResult = positionData[i * 2 + 1]
        const collateralShares = collateralResult?.status === 'success' ? (collateralResult.result as bigint) : 0n
        const debtOwed = debtResult?.status === 'success' ? (debtResult.result as bigint) : 0n
        map.set(`${pair.collateral}-${pair.liability}`, { collateralShares, debtOwed })
      }
    }
    return map
  }, [pairs, positionData, account])

  // Fetch USD prices for all vaults via VaultLens — reads each vault's own oracle
  const vaultLensPriceContracts = useMemo(() => {
    return vaultAddresses.map((address) => ({
      address: VAULT_LENS_ADDRESS as Address,
      abi: vaultLensAbi,
      functionName: 'getVaultInfoFull' as const,
      args: [address] as const,
      chainId: BORROW_VAULT_CHAIN_ID,
    }))
  }, [vaultAddresses])

  const { data: vaultLensPriceData } = useReadContracts({
    contracts: vaultLensPriceContracts,
    query: {
      enabled: vaultAddresses.length > 0,
      staleTime: 30_000,
      placeholderData: keepPreviousData,
    },
  })

  // Build map of underlying token address -> USD price from VaultLens results
  const addressToPrice = useMemo(() => {
    const map = new Map<Address, number>()
    if (!vaultLensPriceData) return map

    for (let i = 0; i < vaultAddresses.length; i++) {
      const vault = vaultAddresses[i]
      const result = vaultLensPriceData[i]
      if (!vault || result?.status !== 'success' || !result.result) continue

      const priceUSD = extractVaultLensPrice(result.result)
      const underlying = vaultToUnderlying.get(vault)
      if (underlying && priceUSD > 0) {
        map.set(underlying, priceUSD)
      }
    }
    return map
  }, [vaultAddresses, vaultLensPriceData, vaultToUnderlying])

  // ============ USER POSITION FETCHING ============

  // Get first vault to fetch EVC address
  const firstVaultAddress = vaultAddresses[0]
  const { data: evcAddressData } = useReadContract({
    address: firstVaultAddress,
    abi: eVaultImplementationAbi,
    functionName: 'EVC',
    chainId: BORROW_VAULT_CHAIN_ID,
    query: {
      enabled: !!firstVaultAddress,
      staleTime: Number.POSITIVE_INFINITY,
    },
  })
  const evcAddress = evcAddressData as Address | undefined

  // Generate sub-account addresses for the user
  // Include fallback range (0-15) for safety, plus any higher active subaccounts from discovery
  const subAccountAddresses = useMemo(() => {
    if (!account || account === zeroAddress) return []
    const fallbackIds = Array.from({ length: FALLBACK_SUBACCOUNT_RANGE }, (_, i) => i)
    const idsToQuery = [...new Set([...fallbackIds, ...activeSubaccountIds])]
    return idsToQuery.map((id) => ({
      subAccountId: id,
      subAccountAddress: getSubAccountAddress(account, id),
    }))
  }, [account, activeSubaccountIds])

  // Query controllers and collaterals for each sub-account
  const subAccountQueryContracts = useMemo(() => {
    if (!evcAddress || !account || account === zeroAddress) return []
    return subAccountAddresses.flatMap(({ subAccountAddress }) => [
      {
        address: evcAddress,
        abi: eulerVaultConnectorAbi,
        functionName: 'getControllers' as const,
        args: [subAccountAddress] as const,
        chainId: BORROW_VAULT_CHAIN_ID,
      },
      {
        address: evcAddress,
        abi: eulerVaultConnectorAbi,
        functionName: 'getCollaterals' as const,
        args: [subAccountAddress] as const,
        chainId: BORROW_VAULT_CHAIN_ID,
      },
    ])
  }, [evcAddress, account, subAccountAddresses])

  const { data: subAccountData } = useReadContracts({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    contracts: subAccountQueryContracts as any,
    query: {
      enabled: subAccountQueryContracts.length > 0,
      staleTime: 30_000,
      placeholderData: keepPreviousData,
    },
  })

  // Parse sub-account data to find active positions (as collateral-liability pairs)
  const activeSubAccountPositions = useMemo(() => {
    const result: Array<{
      subAccountId: number
      subAccountAddress: Address
      liability: Address
      collaterals: Address[]
    }> = []
    if (!subAccountData || !account || account === zeroAddress) return result

    // Safety check: ensure data length matches expected length to handle keepPreviousData race conditions
    // Each subaccount has 2 queries (getControllers + getCollaterals)
    const expectedLength = subAccountAddresses.length * 2
    if (subAccountData.length !== expectedLength) return result

    for (let i = 0; i < subAccountAddresses.length; i++) {
      const subAccountEntry = subAccountAddresses[i]
      if (!subAccountEntry) continue

      const { subAccountId, subAccountAddress } = subAccountEntry
      const dataIndex = i * 2
      const controllers = (subAccountData[dataIndex]?.result as Address[]) || []
      const collaterals = (subAccountData[dataIndex + 1]?.result as Address[]) || []

      // For each controller (liability), track which collaterals are enabled
      for (const liability of controllers) {
        result.push({ subAccountId, subAccountAddress, liability, collaterals })
      }
    }
    return result
  }, [subAccountData, account, subAccountAddresses])

  // Build contracts to fetch debtOf and balanceOf for active positions
  const userPositionContracts = useMemo(() => {
    if (activeSubAccountPositions.length === 0) return []

    const contracts: Array<{
      address: Address
      abi: typeof eVaultImplementationAbi
      functionName: 'debtOf' | 'balanceOf' | 'convertToAssets'
      args?: readonly [Address] | readonly [bigint]
      chainId: number
      _subAccount: Address
      _vault: Address
      _type: 'debt' | 'balance' | 'sharePrice'
    }> = []

    // Get unique vaults (both liabilities and collaterals)
    const uniqueVaults = new Set<Address>()
    for (const pos of activeSubAccountPositions) {
      uniqueVaults.add(pos.liability)
      for (const collateral of pos.collaterals) {
        uniqueVaults.add(collateral)
      }
    }

    // Add share price queries for each unique vault
    for (const vault of uniqueVaults) {
      contracts.push({
        address: vault,
        abi: eVaultImplementationAbi,
        functionName: 'convertToAssets' as const,
        args: [BigInt(10 ** 18)] as const,
        chainId: BORROW_VAULT_CHAIN_ID,
        _subAccount: zeroAddress,
        _vault: vault,
        _type: 'sharePrice',
      })
    }

    // Add debtOf for liabilities and balanceOf for collaterals
    for (const { subAccountAddress, liability, collaterals } of activeSubAccountPositions) {
      // Fetch debt for liability
      contracts.push({
        address: liability,
        abi: eVaultImplementationAbi,
        functionName: 'debtOf' as const,
        args: [subAccountAddress] as const,
        chainId: BORROW_VAULT_CHAIN_ID,
        _subAccount: subAccountAddress,
        _vault: liability,
        _type: 'debt',
      })

      // Fetch balance for each collateral
      for (const collateral of collaterals) {
        contracts.push({
          address: collateral,
          abi: eVaultImplementationAbi,
          functionName: 'balanceOf' as const,
          args: [subAccountAddress] as const,
          chainId: BORROW_VAULT_CHAIN_ID,
          _subAccount: subAccountAddress,
          _vault: collateral,
          _type: 'balance',
        })
      }
    }

    return contracts
  }, [activeSubAccountPositions])

  const { data: userPositionData } = useReadContracts({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    contracts: userPositionContracts.map(({ _subAccount: _, _vault: __, _type: ___, ...c }) => c) as any,
    query: {
      enabled: userPositionContracts.length > 0,
      staleTime: 30_000,
      placeholderData: keepPreviousData,
    },
  })

  // Aggregate user positions by collateral-liability pair
  const userPositionsByPair = useMemo(() => {
    // Key: "collateral-liability" (lowercase addresses)
    const result = new Map<
      string,
      { debt: bigint; collateralBalance: bigint; collateralSharePrice: bigint; firstSubAccountWithPosition?: number }
    >()
    if (!userPositionData || userPositionContracts.length === 0 || activeSubAccountPositions.length === 0) return result

    // First pass: collect share prices
    const sharePrices = new Map<string, bigint>()
    userPositionContracts.forEach((contract, idx) => {
      if (contract._type === 'sharePrice') {
        const value = userPositionData[idx]?.result as bigint | undefined
        if (value) {
          sharePrices.set(contract._vault.toLowerCase(), value)
        }
      }
    })

    // Second pass: collect debt and balance data by sub-account and vault
    const subAccountDebt = new Map<string, Map<string, bigint>>() // subAccount -> liability -> debt
    const subAccountBalance = new Map<string, Map<string, bigint>>() // subAccount -> collateral -> balance

    userPositionContracts.forEach((contract, idx) => {
      if (contract._type === 'sharePrice') return
      const value = userPositionData[idx]?.result as bigint | undefined
      if (!value || value === 0n) return

      const subAccountKey = contract._subAccount.toLowerCase()
      const vaultKey = contract._vault.toLowerCase()

      if (contract._type === 'debt') {
        if (!subAccountDebt.has(subAccountKey)) {
          subAccountDebt.set(subAccountKey, new Map())
        }
        subAccountDebt.get(subAccountKey)!.set(vaultKey, value)
      } else if (contract._type === 'balance') {
        if (!subAccountBalance.has(subAccountKey)) {
          subAccountBalance.set(subAccountKey, new Map())
        }
        subAccountBalance.get(subAccountKey)!.set(vaultKey, value)
      }
    })

    // Third pass: build pair positions from sub-account data
    // Match dashboard behavior: only use the FIRST collateral with balance per debt position
    for (const { subAccountId, subAccountAddress, liability, collaterals } of activeSubAccountPositions) {
      const subAccountKey = subAccountAddress.toLowerCase()
      const liabilityKey = liability.toLowerCase()

      const debt = subAccountDebt.get(subAccountKey)?.get(liabilityKey) || 0n
      if (debt === 0n) continue // Skip if no debt

      // Find the FIRST collateral with balance (matches dashboard behavior)
      for (const collateral of collaterals) {
        const collateralKey = collateral.toLowerCase()
        const balance = subAccountBalance.get(subAccountKey)?.get(collateralKey) || 0n

        if (balance > 0n) {
          const pairKey = `${collateralKey}-${liabilityKey}`

          if (!result.has(pairKey)) {
            result.set(pairKey, {
              debt: 0n,
              collateralBalance: 0n,
              collateralSharePrice: sharePrices.get(collateralKey) || BigInt(10 ** 18),
              firstSubAccountWithPosition: subAccountId,
            })
          }

          const pairData = result.get(pairKey)!
          pairData.debt += debt
          pairData.collateralBalance += balance
          break // Only use the first collateral with balance, like the dashboard
        }
      }
    }

    return result
  }, [userPositionData, userPositionContracts, activeSubAccountPositions])

  // Enrich pairs with token data
  const enrichedPairs = useMemo(() => {
    // Get entities once outside the loop for efficiency
    const entities = getEntities()

    return pairs.map((pair) => {
      // Get underlying asset addresses
      const collateralUnderlying = vaultToUnderlying.get(pair.collateral)
      const liabilityUnderlying = vaultToUnderlying.get(pair.liability)

      // Get token info from Euler indexer
      const collateralTokenInfo = getTokenFromMap(indexerTokens, collateralUnderlying)
      const liabilityTokenInfo = getTokenFromMap(indexerTokens, liabilityUnderlying)

      const collateralSymbol = collateralTokenInfo?.symbol
      const collateralDecimals = collateralTokenInfo?.decimals
      const liabilitySymbol = liabilityTokenInfo?.symbol
      const liabilityDecimals = liabilityTokenInfo?.decimals

      // Get balance for collateral underlying asset from onchain data
      const collateralBalance = collateralUnderlying ? addressToBalance.get(collateralUnderlying) : undefined

      // Use vault data stored directly in the pair (avoids lookup issues)
      const pairWithData = pair as typeof pair & {
        _cash?: bigint
        _borrows?: bigint
        _borrowAPY?: bigint
        _supplyAPY?: bigint
      }

      // Calculate APY from raw interest rate using compound interest formula
      // Interest rate from contract is per-second and scaled by 1e27
      const SECONDS_PER_YEAR = 365.25 * 24 * 60 * 60
      const ratePerSecond = pairWithData._borrowAPY ? Number(pairWithData._borrowAPY) / 1e27 : 0
      const borrowAPY = pairWithData._borrowAPY ? ((1 + ratePerSecond) ** SECONDS_PER_YEAR - 1) * 100 : undefined
      const liabilitySupplyAPY = pairWithData._supplyAPY ? (Number(pairWithData._supplyAPY) / 1e18) * 100 : undefined

      // ============ COLLATERAL SUPPLY APY FROM INDEXER ============
      // Get totalApy directly from Euler indexer - this already includes:
      // - Base lending APY (borrowAPY * utilization * (1 - fee))
      // - Intrinsic APY for yield-bearing tokens (USDH, PT tokens, staked assets, etc.)
      // This matches what the detail view calculates
      const collateralVaultData = indexerVaults?.get(pair.collateral)
      const collateralSupplyAPY = collateralVaultData?.totalApy

      // Use collateral supply APY if available and positive (provides yield benefit),
      // otherwise fall back to liability supply APY. This matches useBorrowDetails.ts behavior.
      // Note: We intentionally skip negative/zero collateral APY as it doesn't provide yield benefit.
      const supplyAPY =
        collateralSupplyAPY !== null && collateralSupplyAPY !== undefined && collateralSupplyAPY > 0
          ? collateralSupplyAPY
          : liabilitySupplyAPY || 0

      const cash = pairWithData._cash || 0n
      const borrows = pairWithData._borrows || 0n
      const totalAssets = cash + borrows
      const utilization = totalAssets > 0n ? Number((borrows * 10000n) / totalAssets) / 100 : undefined
      const liquidity = pairWithData._cash

      // Calculate liquidity in USD
      const liabilityPriceUSD = liabilityUnderlying ? addressToPrice.get(liabilityUnderlying) : undefined
      const collateralPriceUSD = collateralUnderlying ? addressToPrice.get(collateralUnderlying) : undefined
      const liquidityTokens =
        liquidity !== undefined && liabilityDecimals !== undefined
          ? Number(formatUnits(liquidity, liabilityDecimals))
          : undefined
      const liquidityUSD =
        liquidityTokens !== undefined && liabilityPriceUSD !== undefined
          ? liquidityTokens * liabilityPriceUSD
          : undefined

      // Get user position data for this pair (main account only)
      const positionKey = `${pair.collateral}-${pair.liability}`
      const positionInfo = pairToPosition.get(positionKey)
      const userCollateralShares = positionInfo?.collateralShares
      const userDebtOwed = positionInfo?.debtOwed

      // Get user position data for this exact pair (aggregated from sub-accounts)
      const pairKey = `${pair.collateral.toLowerCase()}-${pair.liability.toLowerCase()}`
      const pairPositionData = userPositionsByPair.get(pairKey)

      // User has an active position if they have DEBT (borrowed) - not just deposited collateral
      // This ensures "My Positions" only shows pairs where user is actively borrowing
      const hasPosition = Boolean(
        (userDebtOwed && userDebtOwed > 0n) || (pairPositionData && pairPositionData.debt > 0n),
      )

      // Calculate user borrow amount (debt for this exact pair)
      let userBorrowAmount: number | undefined
      let userBorrowAmountUsd: number | undefined
      if (pairPositionData && pairPositionData.debt > 0n && liabilityDecimals !== undefined) {
        userBorrowAmount = Number(formatUnits(pairPositionData.debt, liabilityDecimals))
        if (liabilityPriceUSD !== undefined) {
          userBorrowAmountUsd = userBorrowAmount * liabilityPriceUSD
        }
      }

      // Calculate user collateral deposit (balance for this exact pair, converted to assets)
      let userCollateralDeposit: number | undefined
      let userCollateralDepositUsd: number | undefined
      if (pairPositionData && pairPositionData.collateralBalance > 0n && collateralDecimals !== undefined) {
        const collateralAssets =
          (pairPositionData.collateralBalance * pairPositionData.collateralSharePrice) / BigInt(10 ** 18)
        userCollateralDeposit = Number(formatUnits(collateralAssets, collateralDecimals))
        if (collateralPriceUSD !== undefined) {
          userCollateralDepositUsd = userCollateralDeposit * collateralPriceUSD
        }
      }
      // Get market for both collateral and liability vaults (indexer-first)
      const collateralMarket = getVaultMarket(pair.collateral, indexerVaults?.get(pair.collateral)?.products)
      const liabilityMarket = getVaultMarket(pair.liability, indexerVaults?.get(pair.liability)?.products)

      // Get curator names from governor addresses (same approach as details page)
      const collateralGovernor = vaultToGovernor.get(pair.collateral)
      const liabilityGovernor = vaultToGovernor.get(pair.liability)

      const collateralCuratorName = collateralGovernor
        ? selectGovernorsFromAddresses(collateralGovernor, entities).governors[0]?.name
        : undefined
      const liabilityCuratorName = liabilityGovernor
        ? selectGovernorsFromAddresses(liabilityGovernor, entities).governors[0]?.name
        : undefined

      const partnerPoints = collateralSymbol
        ? (eulerPartnerPoints[collateralSymbol] as IsolatedPartnerPointsPerPair[] | undefined)
        : undefined

      // Calculate health factor for positions with debt
      // HF = (collateralValueUsd * liquidationLTV) / debtValueUsd
      // liquidationLTV is in basis points (e.g., 8500 = 85%), so divide by 10000
      let healthFactor: number | undefined
      if (
        userCollateralDepositUsd !== undefined &&
        userCollateralDepositUsd > 0 &&
        userBorrowAmountUsd !== undefined &&
        userBorrowAmountUsd > 0 &&
        pair.liquidationLTV !== undefined
      ) {
        const liqLtvDecimal = Number(pair.liquidationLTV) / 10000
        healthFactor = (userCollateralDepositUsd * liqLtvDecimal) / userBorrowAmountUsd
      }

      // Precompute max ROE for leverage mode - single source of truth for sorting and display
      const maxMultiplier = calculateMaxMultiplier(pair.maxLTV)
      const maxRoe = calculateMaxRoe(supplyAPY, borrowAPY, maxMultiplier)

      return {
        ...pair,
        collateralUnderlying,
        liabilityUnderlying,
        collateralSymbol,
        collateralDecimals,
        liabilitySymbol,
        liabilityDecimals,
        collateralTokenInfo,
        liabilityTokenInfo,
        collateralBalance,
        borrowAPY,
        supplyAPY,
        collateralSupplyAPY: collateralSupplyAPY ?? undefined,
        liabilitySupplyAPY,
        maxRoe,
        utilization,
        liquidity,
        liquidityUSD,
        hasPosition,
        userCollateralShares,
        userDebtOwed,
        userBorrowAmount,
        userBorrowAmountUsd,
        userCollateralDeposit,
        userCollateralDepositUsd,
        collateralCuratorName,
        collateralMarket,
        liabilityCuratorName,
        liabilityMarket,
        firstSubAccountWithPosition: pairPositionData?.firstSubAccountWithPosition,
        partnerPoints,
        healthFactor,
      }
    })
  }, [
    pairs,
    indexerTokens,
    vaultToUnderlying,
    addressToBalance,
    addressToPrice,
    userPositionsByPair,
    vaultToGovernor,
    pairToPosition,
    indexerVaults,
  ])

  // Check if we have vault data loaded
  const hasVaultData = vaults.some((v) => v.data !== undefined)
  const isWaitingForVaultData = vaultList.length > 0 && !hasVaultData

  // Only show loading on initial load (when we have no data yet)
  // Once we have pairs, don't show loading on refetch
  const hasData = enrichedPairs.length > 0 && enrichedPairs.some((p) => p.collateralSymbol)
  const isInitialLoading =
    !hasData &&
    (isVaultListLoading || isWaitingForVaultData || isUnderlyingLoading || isTokensLoading || isBalanceLoading)

  // Also consider it loading if we have pairs but no symbols yet (prevents flash of empty state)
  const hasPartialData = enrichedPairs.length > 0 && !enrichedPairs.some((p) => p.collateralSymbol)
  const isLoadingSymbols = hasPartialData && (isTokensLoading || isUnderlyingLoading)

  return {
    pairs: enrichedPairs,
    isLoading: isInitialLoading || isLoadingSymbols,
    error: null,
  }
}
